import { useLocation } from 'react-router-dom';

import { useGetBulkRequestQuery, useGetBulkRequestPaymentSummaryQuery } from '../slices/apis/bulkRequestApi';
import { useGetRequestQuery, useGetRequestPaymentSummaryQuery } from '../slices/apis/requestApi';
import { useRole } from './useRole';
import { Request } from '../models/request.types';

const useRequestRulesBase = (
	reqId: string,
	useRequestQuery: typeof useGetRequestQuery,
	usePaymentSummaryQuery: typeof useGetRequestPaymentSummaryQuery,
) => {
	const { isUser, isAdmin } = useRole();
	const { pathname } = useLocation();
	const { data } = useRequestQuery(reqId, {
		skip: !reqId,
	});
	const { data: paymentSummary } = usePaymentSummaryQuery(reqId, {
		skip: !reqId,
	});

	if (!data || !paymentSummary) {
		return {
			allowQtyEditing: false,
			allowPayment: false,
			allowCancellation: false,
		};
	}

	const allowQtyEditing = getAllowQtyEditing(pathname, isAdmin, isUser, data.paymentStatus);
	const allowPayment = paymentSummary?.remaining > 0 && data.status !== Request.status.cancelled;

	const allowCancellation = [
		Request.status.readyForPayment,
		Request.status.readyForSchedule,
		Request.status.readyForAssessment,
	].includes(data.status);

	return {
		allowQtyEditing,
		allowPayment,
		allowCancellation,
	};
};

function getAllowQtyEditing(pathname: string, isAdmin: boolean, isUser: boolean, status: string | undefined) {
	if (pathname.includes('new')) return true;

	if (isAdmin) return true;

	if (isUser && status === Request.paymentStatus.unpaid) return true;

	return false;
}

export const useBulkRequestRules = (reqId = '') =>
	useRequestRulesBase(reqId, useGetBulkRequestQuery, useGetBulkRequestPaymentSummaryQuery);

export const useRequestRules = (reqId = '') =>
	useRequestRulesBase(reqId, useGetRequestQuery, useGetRequestPaymentSummaryQuery);
